import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { updateTripChatItem } from 'tripPlanner/api'
import type {
  TransportOption,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
  UpdateTripChatItemResponse,
} from 'tripPlanner/api/types'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import type { TripListStatusValue } from '../components/ListView/tripListTypes'
import { updateTripItineraryItem } from '../utils/tripListItineraryItemUpdateUtils'

export interface UpdateTripListStatusParams {
  rowId: string
  itemId: number
  status: TripListStatusValue
  // FE-only, never sent to the API (mutationFn only reads itemId/status) -- carried on the
  // mutation variables so onSuccess below can build an activity entry without a second lookup,
  // mirroring useFeedback.ts's onSuccess-callback pattern for the same "gate on confirmed
  // success" rule already applied to every other producer in this series.
  title?: string
  previousStatus?: TripListStatusValue
  // FE-only, never sent to the API -- carried on the mutation variables so onSuccess below can
  // build an activity entry timestamped to when the customer picked the status, not to when this
  // mutation's request round-trip happened to finish.
  occurredAt?: string
}

interface MutationContext {
  previousItinerary?: TripItineraryData
}

function updateItemStatus<Item extends TripItineraryItem | TripItemWithoutDay>(
  item: Item,
  itemId: number,
  status: TripListStatusValue,
): Item {
  if (item.id !== itemId || item.status === status) return item
  return { ...item, status }
}

function updateTransportOptionStatus(
  option: TransportOption,
  itemId: number,
  status: TripListStatusValue,
): TransportOption {
  if (option.itemId !== itemId || option.status === status) return option
  return { ...option, status }
}

export function updateTripItineraryItemStatus(
  itineraryData: TripItineraryData | undefined,
  itemId: number,
  status: TripListStatusValue,
): TripItineraryData | undefined {
  return updateTripItineraryItem(itineraryData, {
    updateItem: (item) => updateItemStatus(item, itemId, status),
    updateTransportOption: (option) => updateTransportOptionStatus(option, itemId, status),
  })
}

export function useTripListStatusUpdate(
  chatId: number | undefined,
  region: string | undefined,
  onStatusUpdated?: (params: UpdateTripListStatusParams) => void,
) {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const [pendingRowId, setPendingRowId] = useState<string | undefined>(undefined)
  const itineraryQueryKey = ChatKeys.itineraryDetail(chatId ?? 0, region)

  const { mutate, isPending: isUpdating } = useMutation<UpdateTripChatItemResponse, unknown, UpdateTripListStatusParams, MutationContext>({
    mutationFn: async(params) => updateTripChatItem(params.itemId, { status: params.status }),
    onMutate: async(params) => {
      setPendingRowId(params.rowId)
      await queryClient.cancelQueries({ queryKey: itineraryQueryKey })
      const previousItinerary = queryClient.getQueryData<TripItineraryData>(itineraryQueryKey)
      queryClient.setQueryData<TripItineraryData | undefined>(
        itineraryQueryKey,
        (previous) => updateTripItineraryItemStatus(previous, params.itemId, params.status),
      )
      return { previousItinerary }
    },
    onSuccess: (_data, params) => {
      onStatusUpdated?.(params)
    },
    onError: (_error, _params, context) => {
      queryClient.setQueryData(itineraryQueryKey, context?.previousItinerary)
      showSnackbar(
        intl.formatMessage({
          id: 'JDqmDozqUoyg0llJiGMU',
          defaultMessage: 'Failed to update status. Please try again.',
          description: 'Snackbar error shown when saving an itinerary list row status fails in TripChat',
        }),
        'critical',
      )
    },
    onSettled: async() => {
      setPendingRowId(undefined)
      await queryClient.invalidateQueries({ queryKey: itineraryQueryKey })
    },
  })

  const updateStatus = useCallback((params: UpdateTripListStatusParams) => {
    if (!chatId) return
    mutate(params)
  }, [chatId, mutate])

  return {
    isUpdating,
    pendingRowId,
    updateStatus,
  }
}
